import {
	CUSTOMER_BEHAVIOR_SWITCHING_BREAKDOWN,
	CUSTOMER_BEHAVIOR_SWITCHING_SWITCHING_BY_PRODUCTS,
} from "constants/paths"
import { IntlShape } from "react-intl"
import {
	DEFAULT_NAME_FATHER_NODE,
	ERROR_CODE_TIMEOUT,
	FiltersParameters,
	LABEL_DEFAULT_FILTER_SELECT,
	STATUS_CODE_TIMEOUT,
	TYPE_SELECT_PERIOD,
	BROWSER,
} from "./constants"
import { LOCATION_GROUP_FILTER } from "components/Shared/GlobalFilter/constants"
import { FilterTypes } from "components/Shared/GlobalFilter/types"
import {
	InformationNodeChildren,
	InformationNodeGroup,
	NodeDatum,
	OptionsFiltersSelect,
	OptionsSelectDate,
	ResponseInformationNode,
} from "./interfaces"
import { NO_DATA } from "components/Shared/CardTree/constants"

export const titleTreeSwitching = {
	id: "phronesis.performance.application.switching.title.treecard",
}

export const optionsFilterToTitleFatherNode = [
	{
		name: "categories",
		id:
			"phronesis.performance.application.switching.filters.only.categorie.title",
	},
	{
		name: "subcategories",
		id:
			"phronesis.performance.application.switching.filters.only.subcategorie.title",
	},
	{
		name: "fineline",
		id: "phronesis.performance.application.switching.filters.fineline.title",
	},
]

export const disclaimer = {
	id: "phronesis.performance.application.switching.filters.date.disclaimer",
}

export const textRetryButton = {
	id: "phronesis.error.button.message",
}

export const timeoutErrorDescription = {
	name: "phronesis.error.metrics.title",
	message: "phronesis.error.timeout.message",
}

export const metricsErrorDescription = {
	name: "phronesis.error.metrics.title",
	message: "phronesis.error.metrics.message",
}

export const noDataError = {
	name: "phronesis.error.nodata.title",
	message: "phronesis.error.nodata.message",
}

export const optionsSelectDate = [
	{
		code: "CURRENT_MONTH",
		id:
			"phronesis.performance.application.switching.filters.date.options.monthCurrentLast",
	},
	{
		code: "CURRENT_QUARTER",
		id:
			"phronesis.performance.application.switching.filters.date.options.quarterCurrentLast",
	},
	{
		code: "CURRENT_YEAR",
		id:
			"phronesis.performance.application.switching.filters.date.options.yearCurrentLast",
	},
]

export const tabs = [
	{
		id: "phronesis.performance.application.switching.switching.breakdown.tab",
		pathname: CUSTOMER_BEHAVIOR_SWITCHING_BREAKDOWN,
	},
	{
		id: "phronesis.performance.application.switching.switching.products.tab",
		pathname: CUSTOMER_BEHAVIOR_SWITCHING_SWITCHING_BY_PRODUCTS,
	},
]

export const getTitleSelectFilter = (filterSelectName: string) => {
	return optionsFilterToTitleFatherNode.find(
		(filterSelect) => filterSelect.name === filterSelectName
	)?.id
}

export const createOptionsSelectDate = (
	options: Array<OptionsSelectDate>,
	intl: IntlShape
) =>
	options.map(({ code, id }) => ({
		code: code,
		name: intl.formatMessage({ id }),
	}))

export const generateInformation = (
	childrenInformation: InformationNodeChildren
): InformationNodeGroup => {
	const { title, profits, losses, difference } = childrenInformation
	return {
		name: title,
		informationNode: [
			{
				description: `phronesis.performance.application.switching.treecard.description.profits.${title}`,
				value: profits,
			},
			{
				description: `phronesis.performance.application.switching.treecard.description.losses.${title}`,
				value: losses,
			},
			{
				description: `phronesis.performance.application.switching.treecard.description.difference.${title}`,
				value: difference,
			},
		],
	}
}

export const validateIsCodeOrStatusTimeout = (
	codeError: string,
	status: string
) => {
	return codeError === ERROR_CODE_TIMEOUT || status === STATUS_CODE_TIMEOUT
}

export const getCodeAndStatusError = (error: any) => {
	if (error && error.response) {
		const { errCode } = error.response.data
		const { status } = error.response
		return { errCode, status }
	} else {
		return { errCode: "", status: "" }
	}
}

export const validateIsNotPeriodSelect = (nameSelect: string) => {
	return nameSelect !== TYPE_SELECT_PERIOD
}

export const validateIsOptionAll = (labelSelect: string) => {
	return LABEL_DEFAULT_FILTER_SELECT.includes(labelSelect)
}

export const validateAndSetFatherLabel = (
	labelSelect: string,
	valueSelect = ""
) => {
	if (labelSelect === FiltersParameters.SUBCATEGORIES) {
		return FiltersParameters.CATEGORIES
	} else {
		return validateIsOptionAll(valueSelect)
			? FiltersParameters.CATEGORIES
			: FiltersParameters.SUBCATEGORIES
	}
}

export const validateAndSetFatherNodeValue = (
	labelSelect = "",
	optionsFiltersSelect: OptionsFiltersSelect = {}
) => {
	return labelSelect === FiltersParameters.CATEGORIES
		? optionsFiltersSelect?.categories
		: optionsFiltersSelect?.subcategories
}

export const validateAndUpdateNodeValues = (
	titleFilterSelect = "",
	optionSelect = "",
	optionsFiltersSelect: OptionsFiltersSelect = {}
) => {
	const name = validateIsOptionAll(optionSelect)
		? validateAndSetFatherLabel(
				titleFilterSelect,
				optionsFiltersSelect?.subcategories
		  )
		: titleFilterSelect

	const description = validateIsOptionAll(optionSelect)
		? validateAndSetFatherNodeValue(name, optionsFiltersSelect)
		: optionSelect

	return {
		name,
		description,
	}
}

const reduceTreeNodeInformation = (
	previous: NodeDatum,
	current: InformationNodeGroup
) => {
	previous[current.name] = current
	return previous
}

export const generateAllNodes = (
	informationCompleteNode: ResponseInformationNode
) => {
	const { switchingTreeMetrics } = informationCompleteNode
	const { details, ...parentNode } = switchingTreeMetrics
	const nodeInformationFather = [generateInformation(parentNode)]
	const nodesInformationChildren = details.map(generateInformation)
	return nodeInformationFather
		.concat(nodesInformationChildren)
		.reduce(reduceTreeNodeInformation, {} as NodeDatum)
}

export const generateTree = (
	nodeTitle = "",
	nameSelect = "",
	optionsFiltersSelect: OptionsFiltersSelect = {},
	informationNodeArray: NodeDatum
) => {
	const textCardFather = validateAndUpdateNodeValues(
		nameSelect,
		nodeTitle,
		optionsFiltersSelect
	)?.description
	return {
		id: "total",
		name: nodeTitle ? nodeTitle : DEFAULT_NAME_FATHER_NODE,
		textCardFather: textCardFather ? textCardFather : DEFAULT_NAME_FATHER_NODE,
		informationNode: informationNodeArray["total"].informationNode,
		children: [
			{
				id: "newclient",
				name:
					"phronesis.performance.application.switching.treecard.title.newclient",
				informationTooltip:
					"phronesis.performance.application.switching.treecard.information.tooltip.newclient",
				informationNode:
					informationNodeArray["new.losses.client"].informationNode,
			},
			{
				id: "switching",
				name:
					"phronesis.performance.application.switching.treecard.title.switching",
				informationTooltip:
					"phronesis.performance.application.switching.treecard.information.tooltip.switching",
				informationNode:
					informationNodeArray["switching.group"].informationNode,
			},
			{
				id: "expansion",
				name:
					"phronesis.performance.application.switching.treecard.title.expansion",
				informationTooltip:
					"phronesis.performance.application.switching.treecard.information.tooltip.expansion",
				informationNode:
					informationNodeArray["expansion.contraction"].informationNode,
			},
		],
		isValidData: validateIsAvailableData(
			informationNodeArray["total"].informationNode[0].value
		),
	}
}
export const validateIsAvailableData = (dataFatherNode: string) => {
	return dataFatherNode !== NO_DATA
}
export const filtersToShow: Array<FilterTypes> = [LOCATION_GROUP_FILTER]

export const detectSafariBrowser = () =>
	navigator.userAgent.indexOf(BROWSER.SAFARI) > -1 &&
	navigator.userAgent.indexOf(BROWSER.CHROME) <= -1
